import { Check } from "lucide-react";
import type { ReactNode } from "react";

export type MarketingTrustPill = string | { label: string; icon?: ReactNode };

type MarketingTrustPillRowsProps = {
  row1?: readonly MarketingTrustPill[];
  row2?: readonly MarketingTrustPill[];
  /** Flat list rendered as a 3×2 grid (homepage hero). */
  pills?: readonly MarketingTrustPill[];
  variant?: "default" | "check";
  className?: string;
};

function pillLabel(pill: MarketingTrustPill): string {
  return typeof pill === "string" ? pill : pill.label;
}

function pillIcon(pill: MarketingTrustPill, variant: "default" | "check"): ReactNode | null {
  if (typeof pill !== "string" && pill.icon) return pill.icon;
  if (variant === "check") {
    return <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400/90 shrink-0" aria-hidden />;
  }
  return null;
}

const wrapPillClassName =
  "inline-flex items-center min-h-8 gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs leading-snug text-slate-600 dark:border-slate-700/45 dark:bg-slate-800/35 dark:text-slate-400";

const gridPillClassName =
  "flex min-h-12 w-full items-center justify-center rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs sm:text-sm font-medium text-center leading-snug text-slate-600 dark:border-slate-700/45 dark:bg-slate-800/35 dark:text-slate-400";

const rowClassName =
  "flex flex-wrap justify-center lg:justify-start gap-1.5 list-none p-0 m-0";

export function MarketingTrustPillRows({
  row1 = [],
  row2 = [],
  pills,
  variant = "default",
  className = "",
}: MarketingTrustPillRowsProps) {
  if (pills && pills.length > 0) {
    return (
      <ul
        className={`mt-6 grid w-full max-w-md grid-cols-2 sm:grid-cols-3 gap-3 list-none p-0 mx-auto lg:mx-0 ${className}`}
      >
        {pills.map((pill) => {
          const label = pillLabel(pill);
          const icon = pillIcon(pill, variant);
          return (
            <li key={label} className={gridPillClassName}>
              {icon ? (
                <span className="inline-flex items-center justify-center gap-1.5">
                  {icon}
                  <span>{label}</span>
                </span>
              ) : (
                <span>{label}</span>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  const renderRow = (items: readonly MarketingTrustPill[], keyPrefix: string) => {
    if (items.length === 0) return null;
    return (
      <ul className={rowClassName}>
        {items.map((pill) => {
          const label = pillLabel(pill);
          return (
            <li key={`${keyPrefix}-${label}`} className={wrapPillClassName}>
              {pillIcon(pill, variant)}
              <span className="min-w-0">{label}</span>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className={`mt-4 flex flex-col gap-1.5 w-full max-w-xl mx-auto lg:mx-0 ${className}`}>
      {renderRow(row1, "r1")}
      {renderRow(row2, "r2")}
    </div>
  );
}
